import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, FileText, Plus, Download, X, ChevronDown, Network, Eye, Sparkles, Trash2 } from "lucide-react";

import {
  createSession,
  deleteArtifact,
  fetchProjectAudio,
  fetchProjectProfile,
  fetchProjectTranscriptions,
  fetchProjectKnowledgeGraph,
  type KnowledgeGraphBuild,
  fetchProjectArtifacts,
  getProjectAudioFileUrl,
  getProjectTranscriptionBundleUrl,
  getProjectKnowledgeGraphViewUrl,
  type ArtifactSummary,
} from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import { cn } from "@/lib/utils";
import { ArtifactCard } from "@/components/project-details/ArtifactCard";
import { DownloadArchiveDialog } from "@/components/project-details/DownloadArchiveDialog";
import { KnowledgeGraphSection } from "@/components/project-details/KnowledgeGraphSection";
import { AudioPlayer } from "@/components/project-details/AudioPlayer";
import { DeleteArtifactDialog } from "@/components/project-details/DeleteArtifactDialog";

const DEFAULT_DURATION_SECONDS = 120;
const MIN_DURATION_SECONDS = 30;

// Le backend reconstruit le graphe après *chaque* transcription
// (`_rebuild_knowledge_graph`, appelé depuis `transcribe_and_store`), et cette
// reconstruction est une requête LLM qui dure bien après que la transcription
// elle-même soit enregistrée. La query doit donc continuer à interroger le
// backend jusqu'à ce que le fichier apparaisse, sinon la section retombe sur son
// placeholder décoratif jusqu'à un rechargement manuel de la page.
const GRAPH_POLL_INTERVAL_MS = 3000;

function isBuildSettled(build: KnowledgeGraphBuild | null | undefined): boolean {
  return build != null && build.status !== "building";
}

/** Le graphe est-il attendu, c'est-à-dire manquant alors qu'il devrait exister ?
 *
 * Condition d'état volontairement, et non détection de la transition
 * « transcription terminée » : l'utilisateur peut très bien ouvrir cette page
 * après coup, sans que la transition ait jamais eu lieu sous ses yeux. Dès
 * qu'une transcription existe, un graphe doit suivre.
 */
function isGraphExpected(
  audioFiles: string[] | undefined,
  transcriptions: { transcriptions: unknown[] } | undefined,
  transcriptionInProgress: boolean
): boolean {
  // Volontairement permissif. Une version précédente exigeait `audioFiles > 0`,
  // mais le listing audio et les transcriptions sont deux sources distinctes :
  // un projet peut avoir des transcriptions alors que le listing est vide
  // (fichier déplacé, supprimé, listing en échec). Dès qu'il existe l'un ou
  // l'autre, un graphe doit finir par arriver, donc on continue d'interroger.
  const total = audioFiles?.length ?? 0;
  const done = transcriptions?.transcriptions.length ?? 0;
  return transcriptionInProgress || done > 0 || total > 0;
}

const formatOptionLabel = (value: string) =>
  value
    .split("_")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return [mins > 0 ? `${mins} min` : "", `${secs.toString().padStart(2, "0")} s`]
    .filter(Boolean)
    .join(" ");
};

export function ProjectDetailsView() {
  const { sessionId, projectName, scenarioTarget, setCurrentStep, setProgress, updateProgress, setSessionId } = useSessionStore();
  const navigate = useNavigate();

  const startNewArtifact = async () => {
    if (!projectName) return;
    const session = await createSession(projectName, "configuration_scenario", scenarioTarget, true);
    setSessionId(session.session_id);
    setProgress({ audioReady: true, transcriptionsReviewed: true, scenariosReady: false, scenarioChosen: false, scenarioEdited: false });
    setCurrentStep("configuration_scenario");
    navigate("/step/configuration_scenario");
  };

  const [notes, setNotes] = useState("");
  const [showFullTranscription, setShowFullTranscription] = useState(false);
  const [transcriptionInProgress, setTranscriptionInProgress] = useState(false);
  const [transcriptionExpanded, setTranscriptionExpanded] = useState(true);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const notesPrefilledFor = useRef<string | null>(null);
  const progressPrefilledFor = useRef<string | null>(null);

  const profileQuery = useQuery({
    queryKey: ["project-profile", projectName],
    queryFn: () => fetchProjectProfile(projectName!),
    enabled: Boolean(projectName)
  });
  const projectAudioQuery = useQuery({
    queryKey: ["project-audio-files", projectName],
    queryFn: () => fetchProjectAudio(projectName!),
    enabled: Boolean(projectName),
  });
  const transcriptionsQuery = useQuery({
    queryKey: ["project-transcriptions", projectName],
    queryFn: () => fetchProjectTranscriptions(projectName!),
    enabled: Boolean(projectName),
    refetchInterval: (query) => {
      if (!transcriptionInProgress) return false;
      const done = query.state.data?.transcriptions.length ?? 0;
      const failed = query.state.data?.failedFiles.length ?? 0;
      const total = projectAudioQuery.data?.length ?? 0;
      return total > 0 && done + failed < total ? 3000 : false;
    },
  });
  const knowledgeGraphQuery = useQuery({
    queryKey: ["project-knowledge-graph", projectName],
    queryFn: () => fetchProjectKnowledgeGraph(projectName!),
    enabled: Boolean(projectName),
    refetchInterval: (query) => {
      const nodes = query.state.data?.graph?.nodes?.length ?? 0;
      if (nodes > 0) return false;
      // Le backend signale explicitement la fin de construction (ready / empty / failed).
      // Sans statut (build null, projet antérieur à ce mécanisme), on garde l'ancien
      // comportement : sondage tant que le graphe est attendu, sans plafond.
      if (isBuildSettled(query.state.data?.build)) return false;
      return isGraphExpected(projectAudioQuery.data, transcriptionsQuery.data, transcriptionInProgress)
        ? GRAPH_POLL_INTERVAL_MS
        : false;
    },
  });
  const artifactsQuery = useQuery({
    queryKey: ["project-artifacts", projectName],
    queryFn: () => fetchProjectArtifacts(projectName!),
    enabled: Boolean(projectName),
  });
  const queryClient = useQueryClient();
  const [artifactToDelete, setArtifactToDelete] = useState<ArtifactSummary | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (artifact: ArtifactSummary) => deleteArtifact(projectName!, artifact.session_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-artifacts", projectName] });
      setArtifactToDelete(null);
    },
  });
  const mockTranscriptionsQuery = useQuery({
    queryKey: ["mock-transcriptions"],
    queryFn: async () => {
      const response = await fetch("/mocks/transcriptions.json");
      if (!response.ok) throw new Error("Impossible de charger le mock transcriptions");
      return response.json() as Promise<{
        transcriptions: Array<{
          file_name: string;
          transcription: string;
          summary?: { global_summary?: string; topics?: Array<{ title: string }> };
        }>;
      }>;
    },
  });
  const mockDetailsQuery = useQuery({
    queryKey: ["project-details-mock"],
    queryFn: async () => {
      const response = await fetch("/mocks/project-details.json");
      if (!response.ok) throw new Error("Impossible de charger le mock project-details");
      return response.json() as Promise<{
        transcriptionBlock?: {
          subtitle?: string;
          fallbackFileName?: string;
          fallbackTopics?: string[];
          fallbackTitle?: string;
          fallbackText?: string;
        };
        knowledgeGraph?: { title?: string; subtitle?: string; keywordsCount?: number };
        artifacts?: { title?: string; subtitle?: string; emptyState?: boolean };
      }>;
    },
  });

  useEffect(() => {
    if (!projectName) {
      notesPrefilledFor.current = null;
      progressPrefilledFor.current = null;
    }
  }, [projectName]);

  useEffect(() => {
    setTranscriptionExpanded(true);
    setShowFullTranscription(false);
  }, [projectName]);

  useEffect(() => {
    if (projectAudioQuery.isLoading || transcriptionsQuery.isLoading || knowledgeGraphQuery.isLoading) return;
    const total = projectAudioQuery.data?.length ?? 0;
    const done = transcriptionsQuery.data?.transcriptions.length ?? 0;
    // Les fichiers en échec ne seront jamais transcrits : les compter comme
    // traités, sinon l'étape reste « en cours » indéfiniment (même règle que le
    // refetchInterval de transcriptionsQuery ci-dessus).
    const failed = transcriptionsQuery.data?.failedFiles.length ?? 0;
    // If knowledge graph is already built, transcription was completed at some point
    // (even if Parquet data is missing for older sessions)
    const graphIsBuilt =
      (knowledgeGraphQuery.data?.events.length ?? 0) > 0 ||
      (knowledgeGraphQuery.data?.graph.nodes.length ?? 0) > 0;
    setTranscriptionInProgress(total > 0 && done + failed < total && !graphIsBuilt);
  }, [
    projectAudioQuery.data, projectAudioQuery.isLoading,
    transcriptionsQuery.data, transcriptionsQuery.isLoading,
    knowledgeGraphQuery.data, knowledgeGraphQuery.isLoading,
  ]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ text: string }>).detail;
      if (typeof detail?.text === "string" && detail.text.length > 0) {
        setNotes(detail.text);
        (window as Window & { __projectNotes?: string }).__projectNotes = detail.text;
        // Reset guard so query refetch (invalidateQueries fallback) also applies fresh data
        notesPrefilledFor.current = null;
      }
    };
    window.addEventListener("project-notes-updated", handler);
    return () => window.removeEventListener("project-notes-updated", handler);
  }, []);

  useEffect(() => {
    if (!projectName || !profileQuery.data) return;
    if (notesPrefilledFor.current === projectName) return;
    setNotes(profileQuery.data.project_notes ?? "");
    (window as Window & { __projectNotes?: string }).__projectNotes = profileQuery.data.project_notes ?? "";
    notesPrefilledFor.current = projectName;
  }, [profileQuery.data, projectName]);

  // Removed project preferences pre-filling logic

  useEffect(() => {
    if (!projectName || !profileQuery.data) return;
    if (progressPrefilledFor.current === projectName) return;
    // Seuls l'audio et les transcriptions sont partagés au niveau projet ; l'état
    // des scénarios appartient à l'artefact courant (dérivé de la session).
    const hasTranscriptions = Boolean(transcriptionsQuery.data?.transcriptions.length);
    const hasProjectAudio = Boolean(projectAudioQuery.data?.length);
    updateProgress({
      audioReady: Boolean(
        profileQuery.data.final_audio?.path ||
          profileQuery.data.audio_selection?.voices?.length ||
          hasTranscriptions ||
          hasProjectAudio
      ),
      transcriptionsReviewed: Boolean(profileQuery.data.final_audio?.path) || hasTranscriptions,
    });
    progressPrefilledFor.current = projectName;
  }, [profileQuery.data, projectName, updateProgress, transcriptionsQuery.data, projectAudioQuery.data]);

  // Removed settings form options
  // Use real audio files when available; fall back to mock only when no real files are known.
  const hasRealAudio = (projectAudioQuery.data ?? []).length > 0;
  const audioFiles = projectAudioQuery.data?.length
    ? projectAudioQuery.data
    : hasRealAudio || projectAudioQuery.isLoading
      ? []
      : (mockTranscriptionsQuery.data?.transcriptions.map((t) => t.file_name) ?? []);
  const firstAudioFile = audioFiles[0];
  // Never show mock transcriptions while loading or when real audio files are present.
  const transcriptionEntries = transcriptionsQuery.data?.transcriptions.length
    ? transcriptionsQuery.data.transcriptions
    : hasRealAudio || transcriptionsQuery.isLoading || transcriptionsQuery.isFetching
      ? []
      : (mockTranscriptionsQuery.data?.transcriptions ?? []);
  const firstTranscription =
    transcriptionEntries.find((entry) => entry.file_name === firstAudioFile) ?? transcriptionEntries[0];
  const transcriptionTopics =
    firstTranscription?.summary?.topics?.slice(0, 4).map((topic) => topic.title).filter(Boolean) ?? [];
  const kGraphTags = useMemo(() => {
    const graph = knowledgeGraphQuery.data?.graph;
    if (!graph) return [];
    const nodeMap = Object.fromEntries(graph.nodes.map((n) => [n.id, n]));
    const counts: Record<string, number> = {};
    for (const edge of graph.edges) {
      if (edge.source) counts[edge.source] = (counts[edge.source] ?? 0) + 1;
      if (edge.target) counts[edge.target] = (counts[edge.target] ?? 0) + 1;
    }
    return Object.entries(counts)
      .filter(([id]) => nodeMap[id]?.type !== "Event")
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([id]) => nodeMap[id]?.name)
      .filter(Boolean) as string[];
  }, [knowledgeGraphQuery.data]);
  const fallbackTopics =
    mockDetailsQuery.data?.transcriptionBlock?.fallbackTopics ??
    ["Vie quotidienne", "Mémoire ouvrière", "Chantiers navals", "Patrimoine local"];
  const displayedTags =
    transcriptionTopics.length > 0 ? transcriptionTopics :
    kGraphTags.length > 0 ? kGraphTags :
    fallbackTopics;
  const transcriptionText =
    firstTranscription?.transcription?.trim() ||
    mockDetailsQuery.data?.transcriptionBlock?.fallbackText ||
    "La transcription apparaîtra ici après l’upload et le traitement automatique du fichier audio.";
  const transcriptionTitle = mockDetailsQuery.data?.transcriptionBlock?.fallbackTitle ?? "Souvenir des chantiers de Nantes";
  const transcriptionSubtitle =
    mockDetailsQuery.data?.transcriptionBlock?.subtitle ?? "Lire et modifier la transcription des fichiers audio.";
  const fallbackFileName = mockDetailsQuery.data?.transcriptionBlock?.fallbackFileName ?? "interview_001.mp3";
  const knowledgeGraphTitle = mockDetailsQuery.data?.knowledgeGraph?.title ?? "Knowledge Graph";
  const knowledgeGraphSubtitle =
    mockDetailsQuery.data?.knowledgeGraph?.subtitle ?? "Lire et modifier les transcriptions des fichiers audio.";
  const artifactsTitle = mockDetailsQuery.data?.artifacts?.title ?? "Mes Artefacts";
  const artifactsSubtitle =
    mockDetailsQuery.data?.artifacts?.subtitle ?? "Lire et modifier les transcriptions des fichiers audio.";

  const audioSrc = projectName && firstAudioFile ? getProjectAudioFileUrl(projectName, firstAudioFile) : null;

  // Audio state managed inside the new AudioPlayer component

  const knowledgeGraphNodeCount = knowledgeGraphQuery.data?.graph?.nodes?.length ?? 0;
  const knowledgeGraphKeywordCount =
    knowledgeGraphQuery.data?.graph?.nodes?.filter((n) => n.type === "Keyword").length ?? 0;
  const knowledgeGraphViewUrl = projectName ? getProjectKnowledgeGraphViewUrl(projectName) : null;
  // Même prédicat que le polling : tant que le graphe est attendu, on montre
  // « Construction du graphe… » plutôt que la constellation décorative, qui
  // laisse croire à un graphe vide alors qu'il est simplement en cours d'écriture.
  const knowledgeGraphBuilding =
    knowledgeGraphNodeCount === 0 &&
    !isBuildSettled(knowledgeGraphQuery.data?.build) &&
    (knowledgeGraphQuery.isLoading ||
      isGraphExpected(projectAudioQuery.data, transcriptionsQuery.data, transcriptionInProgress));

  if (!sessionId) {
    return <p className="text-sm text-muted-foreground">Créez ou sélectionnez un projet pour continuer.</p>;
  }

  const openArtifact = (artifact: ArtifactSummary) => {
    const step = artifact.current_step ?? "final_validation";
    // Full step order including implicit frontend-only steps.
    const STEP_ORDER = ["audio_sources", "transcription_review", "configuration_scenario", "choix_scenario", "scenario_review", "edition_text", "scenario_edit", "final_validation"];
    const atOrPast = (s: string) => STEP_ORDER.indexOf(step) >= STEP_ORDER.indexOf(s);
    setSessionId(artifact.session_id);
    setCurrentStep(step);
    setProgress({
      audioReady: atOrPast("audio_sources"),
      transcriptionsReviewed: atOrPast("transcription_review"),
      scenariosReady: atOrPast("choix_scenario"),
      scenarioChosen: atOrPast("edition_text"),
      scenarioEdited: atOrPast("scenario_edit"),
    });
    navigate(`/step/${step}`);
  };



  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6">
      <section className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white">
        <div className="flex items-center justify-between gap-6 border-b-[0.8px] border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="inline-flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#0F172B]" />
              <h3 className="text-[20px] font-semibold leading-none text-[#0F172B]">Transcription</h3>
            </div>
            <p className="text-[14px] font-normal leading-none text-[#45556C]">
              {transcriptionSubtitle}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setTranscriptionExpanded(v => !v)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#45556C] transition-colors hover:bg-[#eef2f7]"
              aria-label={transcriptionExpanded ? "Réduire la transcription" : "Étendre la transcription"}
            >
              <ChevronDown className={cn("h-5 w-5 transition-transform duration-200", !transcriptionExpanded && "-rotate-90")} />
            </button>
            <button
              type="button"
              onClick={startNewArtifact}
              className="inline-flex h-[38px] shrink-0 items-center gap-1 rounded-xl bg-[#007AFF] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#006ae0]"
            >
              <Plus className="h-4 w-4" />
              <span>Créer un artefact</span>
            </button>
          </div>
        </div>

        {transcriptionExpanded && <div className="flex flex-col gap-6 bg-white px-5 py-4">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
            <div className="mb-[18px] flex items-center justify-between gap-3">
              <p className="text-[16px] font-semibold leading-none text-[#0F172B]">
                Fichier 1: {firstAudioFile ?? fallbackFileName}
              </p>
              <div className="inline-flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsDownloadOpen(true)}
                  className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border border-[#E2E8F0] bg-white text-[#45556C] transition-colors hover:bg-[#F8FAFC]"
                  aria-label="Télécharger transcription, événements et graphe"
                >
                  <Download className="h-4 w-4" />
                </button>

              </div>
            </div>

            <AudioPlayer audioSrc={audioSrc} />
          </div>

          <div className="flex flex-wrap gap-2">
            {displayedTags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex min-h-8 h-auto items-center justify-center rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-[6px] text-[14px] font-semibold leading-snug text-[#45556C]"
              >
                {tag}
              </span>
            ))}
          </div>

          {!showFullTranscription ? (
            <div className="flex flex-col gap-[18px]">
              <h4 className="text-[16px] font-semibold leading-none text-[#0F172B]">{transcriptionTitle}</h4>
              <p
                className="text-[14px] font-normal leading-relaxed text-transparent"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  background: "linear-gradient(180deg, #45556C 0%, #F3F4F8 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                }}
              >
                {transcriptionText}
              </p>
              <button
                type="button"
                onClick={() => setShowFullTranscription(true)}
                className="text-xs font-semibold text-[#007AFF] hover:underline self-start"
              >
                Afficher plus
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-[18px]">
              <h4 className="text-[16px] font-semibold leading-none text-[#0F172B]">{transcriptionTitle}</h4>
              <div className="max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                <p className="text-[14px] font-normal leading-relaxed text-[#45556C] whitespace-pre-line">
                  {transcriptionText}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowFullTranscription(false)}
                className="text-xs font-semibold text-[#007AFF] hover:underline self-start"
              >
                Afficher moins
              </button>
            </div>
          )}

          <div className="flex items-center justify-end gap-[14px]">

            <button
              type="button"
              onClick={() => setIsDownloadOpen(true)}
              className="inline-flex h-[38px] items-center gap-1 rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm font-semibold text-[#45556C] transition-colors hover:bg-[#F8FAFC] hover:text-[#0F172B]"
            >
              <Download className="h-4 w-4" />
              <span>Exporter la transcription</span>
            </button>
          </div>
        </div>}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <KnowledgeGraphSection
          title={knowledgeGraphTitle}
          subtitle={knowledgeGraphSubtitle}
          nodeCount={knowledgeGraphNodeCount}
          keywordCount={knowledgeGraphKeywordCount}
          viewUrl={knowledgeGraphViewUrl}
          isBuilding={knowledgeGraphBuilding}
        />

        <article className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
          <div className="flex items-center gap-6 border-b-[0.8px] border-[#E2E8F0] bg-[#F8FAFC] px-5 py-4">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="inline-flex items-center gap-1">
                <Sparkles className="h-5 w-5 text-[#0F172B]" />
                <h3 className="text-[20px] font-semibold leading-none text-[#0F172B]">{artifactsTitle}</h3>
              </div>
              <p className="text-[14px] font-normal leading-none text-[#45556C]">
                {artifactsSubtitle}
              </p>
            </div>
          </div>

          <div className="flex min-h-[405.8px] flex-col bg-white px-5 py-4">
            {artifactsQuery.isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-[#45556C]" />
              </div>
            ) : (artifactsQuery.data ?? []).length === 0 ? (
              <div className="flex flex-1 items-end justify-center pb-2">
                <button
                  type="button"
                  onClick={startNewArtifact}
                  className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-[12px] bg-[#007AFF] text-white shadow-sm transition-colors hover:bg-[#006ae0]"
                  aria-label="Créer un artefact"
                >
                  <Plus className="h-7 w-7" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {(artifactsQuery.data as ArtifactSummary[]).map((artifact) => (
                  <ArtifactCard key={artifact.id} artifact={artifact} onClick={() => openArtifact(artifact)} onDelete={() => setArtifactToDelete(artifact)} />
                ))}
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={startNewArtifact}
                    className="inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-[#007AFF] px-3 text-[13px] font-medium text-white transition-colors hover:bg-[#006ae0]"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Nouvel artefact</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </article>
      </section>

      <DeleteArtifactDialog
        artifact={artifactToDelete}
        onClose={() => setArtifactToDelete(null)}
        onDeleteConfirm={() => artifactToDelete && deleteMutation.mutate(artifactToDelete)}
        isPending={deleteMutation.isPending}
      />

      <DownloadArchiveDialog
        open={isDownloadOpen}
        onClose={() => setIsDownloadOpen(false)}
        projectName={projectName ?? null}
        transcriptionUrl={projectName ? getProjectTranscriptionBundleUrl(projectName) : "#"}
        graphUrl={projectName && knowledgeGraphNodeCount > 0 ? getProjectKnowledgeGraphViewUrl(projectName) : null}
      />
    </div>
  );
}
